import { useEffect, useRef, useState } from "react";
import { Html } from "react-konva-utils";
import { Thread } from "@cord-sdk/react";
import useOutsideRef from "frontend/utils/click-outside-handler";
import { renderInPortal, usePortal } from "frontend/modal/usePortal";
import { PortalID } from "frontend/modal/portal-types";
import style from "./cord-thread-view.module.css";

export default function CordThreadView({
  x,
  y,
  storeId,
  location,
  onClose,
  onMessageCountChange,
}: {
  x: number; // screen X, not stage
  y: number; // screen Y, not stage
  storeId: string;
  location: any;
  onClose: (messageCount?: number) => void;
  onMessageCountChange?: (messageCount: number) => void;
}) {
  const [threadInfo, setThreadInfo] = useState<any>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const [refReady, setRefReady] = useState(false);

  const regularClose = () => onClose(threadInfo?.messageCount);
  const portal = usePortal(PortalID.Comments);

  useOutsideRef(
    threadRef,
    (e: Event) => {
      if (threadRef.current) {
        if (e.type == "keydown" && (e as KeyboardEvent).key == "Escape") {
          regularClose();
          return;
        }
        const cordRoot = threadRef.current;
        let text = cordRoot.lastChild?.lastChild;
        if (cordRoot.contains(e.target as Node)) return;
        while (text && text.nodeType != text.TEXT_NODE) {
          text = text.lastChild;
        }
        if (text && text.textContent && text.textContent != "@") {
          e.preventDefault();
          e.cancelBubble = true;
          cordRoot.classList.add(style.shake);
          setTimeout(() => {
            cordRoot.classList.remove(style.shake);
          }, 1000);
        } else {
          regularClose();
        }
      }
    },
    { preventScroll: true }
  );

  useEffect(() => {
    if (threadRef.current) {
      setTimeout(() => setRefReady(true), 400);
    }
  }, [threadRef.current]);

  useEffect(() => {
    if (refReady) {
      //Focus the thread element in 100msec (give it time to render its children)
      setTimeout(() => {
        if (threadRef.current) {
          const childDivs = threadRef.current.getElementsByTagName("div");
          for (let i = 0; i < childDivs.length; i++) {
            const element = childDivs[i];
            if (element.getAttribute("role") == "textbox") {
              element.focus();
              break;
            }
          }
        }
      }, 100);
    }
  }, [refReady]);

  const element = (
    <>
      <div
        id="cord-thread-view"
        ref={threadRef}
        style={{
          position: "fixed",
          top: y - 80 + "px",
          left: x + 30 + "px",
          zIndex: 100,
        }}
      >
        <Thread
          // pass className to style, disply=block, ...
          threadId={storeId}
          location={location}
          collapsed={false}
          onThreadInfoChange={(info) => {
            setThreadInfo(info);
            onMessageCountChange?.(info.messageCount);
          }}
          showHeader={true}
          onResolved={() => onClose(0)}
          onClose={regularClose}
          style={{ width: "300px", maxHeight: "400px", userSelect: "text" }}
        />
      </div>
    </>
  );

  return <Html transform={false}>{renderInPortal(portal, element)}</Html>;
}
